/**
 * Create a new AudioBuffer containing a portion of the source buffer.
 *
 * @param source - Original AudioBuffer
 * @param startTime - Start time in seconds
 * @param duration - Duration in seconds
 * @param audioContext - AudioContext for creating new buffer
 * @returns New AudioBuffer with the sliced portion
 */
export function sliceAudioBuffer(
  source: AudioBuffer,
  startTime: number,
  duration: number,
  audioContext: AudioContext
): AudioBuffer {
  const sampleRate = source.sampleRate;
  const startSample = Math.floor(startTime * sampleRate);
  const numSamples = Math.min(
    Math.floor(duration * sampleRate),
    source.length - startSample
  );

  if (numSamples <= 0) {
    throw new Error("Invalid slice parameters: would result in empty buffer");
  }

  const newBuffer = audioContext.createBuffer(
    source.numberOfChannels,
    numSamples,
    sampleRate
  );

  for (let channel = 0; channel < source.numberOfChannels; channel++) {
    const sourceData = source.getChannelData(channel);
    const destData = newBuffer.getChannelData(channel);

    // Copy the relevant portion
    for (let i = 0; i < numSamples; i++) {
      destData[i] = sourceData[startSample + i] ?? 0;
    }
  }

  return newBuffer;
}
